import React, { useState } from 'react';
import { Activity, Status, Priority } from '../types';
import { StatusBadge } from '../components/StatusBadge';
import { PriorityBadge } from '../components/PriorityBadge';
import { SearchIcon, DownloadIcon, PlusIcon, EyeIcon, PencilIcon } from 'lucide-react';
interface ActivityListPageProps {
  activities: Activity[];
  onViewActivity: (id: string) => void;
  onAddActivity: () => void;
  onEditActivity: (id: string) => void;
}
export function ActivityListPage({
  activities,
  onViewActivity,
  onAddActivity,
  onEditActivity
}: ActivityListPageProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('Semua Status');
  const [priorityFilter, setPriorityFilter] = useState<string>('Semua Prioritas');
  const [unitFilter, setUnitFilter] = useState<string>('Semua Unit');
  const units = ['Semua Unit', ...Array.from(new Set(activities.map(a => a.unit)))];
  const statuses = ['Semua Status', 'Draft', 'Baru', 'Berjalan', 'Menunggu Verifikasi', 'Tertunda', 'Selesai', 'Dibatalkan'];
  const priorities = ['Semua Prioritas', 'Rendah', 'Sedang', 'Tinggi', 'Sangat Tinggi'];
  const filteredActivities = activities.filter(act => {
    const matchSearch = act.title.toLowerCase().includes(searchTerm.toLowerCase()) || act.code.toLowerCase().includes(searchTerm.toLowerCase()) || act.personInCharge.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'Semua Status' || act.status === statusFilter;
    const matchPriority = priorityFilter === 'Semua Prioritas' || act.priority === priorityFilter;
    const matchUnit = unitFilter === 'Semua Unit' || act.unit === unitFilter;
    return matchSearch && matchStatus && matchPriority && matchUnit;
  });
  const today = new Date().toISOString().split('T')[0];
  return <div className="space-y-6 flex flex-col h-full" data-id="element-385">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-386">
        <h2 className="text-2xl font-bold text-[#E2E8F0] uppercase tracking-wide flex items-center" data-id="element-387">
          <span className="w-2 h-6 bg-[#00D4FF] mr-3" data-id="element-388"></span>
          DAFTAR KEGIATAN OPERASIONAL
        </h2>
        <button onClick={onAddActivity} className="flex items-center px-4 py-2 bg-[#00D4FF] text-[#0B0F1A] rounded hover:bg-[#00D4FF]/90 transition-colors shadow-sm font-bold text-sm uppercase glow-cyan" data-id="element-389">
          <PlusIcon className="w-4 h-4 mr-2" data-id="element-390" />
          TAMBAH KEGIATAN
        </button>
      </div>

      {/* Filters */}
      <div className="bg-[#111827] p-4 rounded shadow-sm border border-[#1E3A5F] flex flex-wrap gap-4 items-center" data-id="element-391">
        <div className="relative flex-1 min-w-[200px]" data-id="element-392">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-393">
            <SearchIcon className="h-4 w-4 text-[#94A3B8]" data-id="element-394" />
          </div>
          <input type="text" placeholder="Cari kode, judul, atau PIC..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-[#1E3A5F] rounded text-sm focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] bg-[#0B0F1A] text-[#E2E8F0] placeholder-[#475569]" data-id="element-395" />
        </div>

        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-[#1E3A5F] rounded text-sm py-2 pl-3 pr-8 focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] bg-[#0B0F1A] text-[#E2E8F0]" data-id="element-396">
          {statuses.map(s => <option key={s} value={s} data-id="element-397">
              {s}
            </option>)}
        </select>

        <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} className="border border-[#1E3A5F] rounded text-sm py-2 pl-3 pr-8 focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] bg-[#0B0F1A] text-[#E2E8F0]" data-id="element-398">
          {priorities.map(p => <option key={p} value={p} data-id="element-399">
              {p}
            </option>)}
        </select>

        <select value={unitFilter} onChange={e => setUnitFilter(e.target.value)} className="border border-[#1E3A5F] rounded text-sm py-2 pl-3 pr-8 focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] bg-[#0B0F1A] text-[#E2E8F0]" data-id="element-400">
          {units.map(u => <option key={u} value={u} data-id="element-401">
              {u}
            </option>)}
        </select>

        <button className="flex items-center px-3 py-2 border border-[#1E3A5F] text-[#E2E8F0] bg-[#1A2332] rounded hover:bg-[#1E3A5F] transition-colors text-sm font-bold uppercase ml-auto" data-id="element-402">
          <DownloadIcon className="w-4 h-4 mr-2 text-[#00D4FF]" data-id="element-403" />
          EKSPOR
        </button>
      </div>

      {/* Data Table */}
      <div className="bg-[#111827] rounded shadow-lg border border-[#1E3A5F] overflow-hidden flex-1" data-id="element-404">
        <div className="overflow-x-auto" data-id="element-405">
          <table className="w-full text-sm text-left" data-id="element-406">
            <thead className="text-xs text-[#94A3B8] uppercase bg-[#0F1629] border-b border-[#1E3A5F]" data-id="element-407">
              <tr data-id="element-408">
                <th className="px-4 py-3 font-semibold w-12 text-center" data-id="element-409">NO</th>
                <th className="px-4 py-3 font-semibold" data-id="element-410">KODE</th>
                <th className="px-4 py-3 font-semibold min-w-[250px]" data-id="element-411">
                  JUDUL KEGIATAN
                </th>
                <th className="px-4 py-3 font-semibold" data-id="element-412">UNIT / PIC</th>
                <th className="px-4 py-3 font-semibold" data-id="element-413">STATUS</th>
                <th className="px-4 py-3 font-semibold" data-id="element-414">PRIORITAS</th>
                <th className="px-4 py-3 font-semibold" data-id="element-415">DEADLINE</th>
                <th className="px-4 py-3 font-semibold min-w-[150px]" data-id="element-416">
                  PROGRES
                </th>
                <th className="px-4 py-3 font-semibold text-center" data-id="element-417">AKSI</th>
              </tr>
            </thead>
            <tbody data-id="element-418">
              {filteredActivities.length > 0 ? filteredActivities.map((act, index) => {
              const isOverdue = act.dueDate < today && act.status !== 'Selesai' && act.status !== 'Dibatalkan';
              return <tr key={act.id} className={`border-b border-[#1E3A5F]/50 hover:bg-[#1A2332] transition-colors ${isOverdue ? 'border-l-2 border-l-[#FF3B3B] bg-[#FF3B3B]/5' : 'border-l-2 border-l-transparent'}`} data-id="element-419">
                      <td className="px-4 py-4 text-center text-[#94A3B8] text-sm" data-id="element-420">
                        {index + 1}
                      </td>
                      <td className="px-4 py-4 font-mono text-[#00D4FF] text-xs whitespace-nowrap" data-id="element-421">
                        {act.code}
                      </td>
                      <td className="px-4 py-4" data-id="element-422">
                        <div className="font-medium text-[#E2E8F0] line-clamp-2 text-sm" data-id="element-423">
                          {act.title}
                        </div>
                      </td>
                      <td className="px-4 py-4" data-id="element-424">
                        <div className="text-[#E2E8F0] text-xs font-semibold mb-1" data-id="element-425">
                          {act.unit}
                        </div>
                        <div className="text-xs text-[#94A3B8]" data-id="element-426">
                          {act.personInCharge}
                        </div>
                      </td>
                      <td className="px-4 py-4" data-id="element-427">
                        <StatusBadge status={act.status} data-id="element-428" />
                      </td>
                      <td className="px-4 py-4" data-id="element-429">
                        <PriorityBadge priority={act.priority} data-id="element-430" />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap" data-id="element-431">
                        <span className={`font-mono text-xs ${isOverdue ? 'text-[#FF3B3B] font-bold glow-red' : 'text-[#E2E8F0]'}`} data-id="element-432">
                          {act.dueDate}
                        </span>
                      </td>
                      <td className="px-4 py-4" data-id="element-433">
                        <div className="flex items-center justify-between mb-1" data-id="element-434">
                          <span className="text-xs font-mono text-[#00D4FF]" data-id="element-435">
                            {act.progressPercent}%
                          </span>
                        </div>
                        <div className="w-full bg-[#0B0F1A] rounded-full h-1.5 border border-[#1E3A5F]" data-id="element-436">
                          <div className={`h-full rounded-full ${act.progressPercent === 100 ? 'bg-[#00FF88] glow-green' : 'bg-[#00D4FF] glow-cyan'}`} style={{
                      width: `${act.progressPercent}%`
                    }} data-id="element-437"></div>
                        </div>
                      </td>
                      <td className="px-4 py-4" data-id="element-438">
                        <div className="flex items-center justify-center space-x-2" data-id="element-439">
                          <button onClick={() => onViewActivity(act.id)} className="p-1.5 text-[#00D4FF]/60 hover:text-[#00D4FF] hover:bg-[#00D4FF]/10 rounded transition-colors" title="Lihat Detail" data-id="element-440">
                            <EyeIcon className="w-4 h-4" data-id="element-441" />
                          </button>
                          <button onClick={() => onEditActivity(act.id)} className="p-1.5 text-[#94A3B8] hover:text-[#E2E8F0] hover:bg-[#1E3A5F] rounded transition-colors" title="Edit" data-id="element-442">
                            <PencilIcon className="w-4 h-4" data-id="element-443" />
                          </button>
                        </div>
                      </td>
                    </tr>;
            }) : <tr data-id="element-444">
                  <td colSpan={9} className="px-4 py-8 text-center text-[#94A3B8] text-sm" data-id="element-445">
                    Tidak ada data kegiatan yang sesuai dengan filter.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        {/* Simple Pagination Footer */}
        <div className="px-4 py-3 border-t border-[#1E3A5F] bg-[#0B0F1A] flex items-center justify-between sm:px-6" data-id="element-446">
          <div className="text-xs text-[#94A3B8]" data-id="element-447">
            Menampilkan{' '}
            <span className="font-medium text-[#E2E8F0]" data-id="element-448">
              {filteredActivities.length}
            </span>{' '}
            kegiatan
          </div>
          <div className="flex space-x-2" data-id="element-449">
            <button className="px-3 py-1 border border-[#1E3A5F] rounded text-xs bg-[#111827] text-[#475569] cursor-not-allowed font-medium" data-id="element-450">
              Sebelumnya
            </button>
            <button className="px-3 py-1 border border-[#1E3A5F] rounded text-xs bg-[#1A2332] text-[#E2E8F0] hover:bg-[#1E3A5F] font-medium transition-colors" data-id="element-451">
              Selanjutnya
            </button>
          </div>
        </div>
      </div>
    </div>;
}